import { useRef, useEffect } from 'react';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { Route, Switch, useLocation, Router as WouterRouter } from 'wouter';
import { ClerkProvider, useClerk } from '@clerk/react';
import { publishableKeyFromHost } from '@clerk/react/internal';

import Home from '@/pages/home';
import Learn from '@/pages/learn';
import ColorDetail from '@/pages/color-detail';
import QuizPersonality from '@/pages/quiz-personality';
import QuizDecision from '@/pages/quiz-decision';
import Privacy from '@/pages/privacy';
import Disclaimer from '@/pages/disclaimer';
import Terms from '@/pages/terms';
import Contact from '@/pages/contact';
import Insights from '@/pages/insights';
import InsightDetail from '@/pages/insight-detail';
import Scan from '@/pages/scan';
import Pulse from '@/pages/pulse';
import DecisionMatrix from '@/pages/decision-matrix';
import AuraScanner from '@/pages/aura-scanner';
import BackgroundMaker from '@/pages/background-maker';
import ColorGrabber from '@/pages/color-grabber';
import Synergy from '@/pages/synergy';
import ColorSandbox from '@/pages/color-sandbox';
import PaintVisualizer from '@/pages/paint-visualizer';
import About from '@/pages/about';
import SignInPage from '@/pages/sign-in';
import SignUpPage from '@/pages/sign-up';
import NotFound from '@/pages/not-found';
import { ColorPulseProvider } from '@/context/color-pulse';
import { BackgroundFX } from '@/components/effects/background-fx';
import { ScrollToTop } from '@/components/ScrollToTop';
import { basePath, clerkAppearance, clerkLocalization } from '@/lib/clerk';

const queryClient = new QueryClient();

// REQUIRED — copy verbatim. Resolves the key from window.location.hostname so the
// same build serves multiple Clerk custom domains.
const clerkPubKey = publishableKeyFromHost(
  window.location.hostname,
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
);

// REQUIRED — copy verbatim. Empty in dev, auto-set in prod.
const clerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL;

if (!clerkPubKey) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY in .env file');
}

// Clerk passes full paths to routerPush/routerReplace, but wouter's
// setLocation prepends the base — strip it to avoid doubling.
function stripBase(path: string): string {
  return basePath && path.startsWith(basePath)
    ? path.slice(basePath.length) || '/'
    : path;
}

// Keeps cached queries (e.g. comments) in sync when the signed-in user changes.
function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const client = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = addListener(({ user }) => {
      const userId = user?.id ?? null;
      if (
        prevUserIdRef.current !== undefined &&
        prevUserIdRef.current !== userId
      ) {
        client.clear();
      }
      prevUserIdRef.current = userId;
    });
    return unsubscribe;
  }, [addListener, client]);

  return null;
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/learn" component={Learn} />
      <Route path="/learn/:id" component={ColorDetail} />
      <Route path="/quiz/personality" component={QuizPersonality} />
      <Route path="/quiz/decision" component={QuizDecision} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/disclaimer" component={Disclaimer} />
      <Route path="/terms" component={Terms} />
      <Route path="/contact" component={Contact} />
      <Route path="/insights" component={Insights} />
      <Route path="/insights/:slug" component={InsightDetail} />
      <Route path="/scan" component={Scan} />
      <Route path="/pulse" component={Pulse} />
      <Route path="/decision-matrix" component={DecisionMatrix} />
      <Route path="/aura-scanner" component={AuraScanner} />
      <Route path="/background-maker" component={BackgroundMaker} />
      <Route path="/color-grabber" component={ColorGrabber} />
      <Route path="/synergy" component={Synergy} />
      <Route path="/color-sandbox" component={ColorSandbox} />
      <Route path="/paint-visualizer" component={PaintVisualizer} />
      <Route path="/about" component={About} />
      {/* REQUIRED — "/sign-in/*?" and "/sign-up/*?" verbatim so OAuth sub-paths match. */}
      <Route path="/sign-in/*?" component={SignInPage} />
      <Route path="/sign-up/*?" component={SignUpPage} />
      <Route component={NotFound} />
    </Switch>
  );
}

function ClerkProviderWithRoutes() {
  const [, setLocation] = useLocation();

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      proxyUrl={clerkProxyUrl}
      appearance={clerkAppearance}
      signInUrl={`${basePath}/sign-in`}
      signUpUrl={`${basePath}/sign-up`}
      localization={clerkLocalization}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
    >
      <QueryClientProvider client={queryClient}>
        <ClerkQueryClientCacheInvalidator />
        <ColorPulseProvider>
          <BackgroundFX />
          <ScrollToTop />
          <Router />
        </ColorPulseProvider>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

function App() {
  return (
    <WouterRouter base={basePath}>
      <ClerkProviderWithRoutes />
    </WouterRouter>
  );
}

export default App;
